import { PRODUCT_LABELS, ROUTES } from "domain/configuration";
import { clearState, setCurrentFormStep } from "domain/core/reducers";
import {
  fetchPromoCookie,
  formatAddress,
  sendAnalyticsCustomEvent,
  sendAnalyticsPageView,
  sendAnalyticsSignUpId,
} from "lib/util";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store/storeHooks";

export function Complete() {
  const dispatch = useAppDispatch();
  const {
    user: { property, details, checks: userChecks },
    selectedAddress: address,
    flickUserId: signUpId,
    estimatedUsageType,
    selectedEligibilityType,
    promoCode,
    leadSource,
  } = useAppSelector((store) => store.coreData);

  const [fgfSuccess, setFgfSuccess] = useState<boolean>(false);
  const [displayPrefName, setDisplayPrefName] = useState<string>("");
  const [displayAddress, setDisplayAddress] = useState<string>("");

  useEffect(() => {
    if (address && !displayAddress) {
      setDisplayAddress(formatAddress(address));
    }

    if (details?.prefName && !displayPrefName) {
      setDisplayPrefName(details.prefName);
    }

    if (promoCode && !fgfSuccess) {
      const cookiePromoCode = fetchPromoCookie();
      setFgfSuccess(Boolean(cookiePromoCode) && cookiePromoCode === promoCode);
      document.cookie =
        "promo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;"; // clear cookie after successful signup
    }
  }, [address, details?.prefName, promoCode]);

  useEffect(() => {
    sendAnalyticsPageView(ROUTES.complete);
    if (signUpId) {
      sendAnalyticsSignUpId(signUpId);
      sendAnalyticsCustomEvent("join_complete", {
        plan_type: details.product && PRODUCT_LABELS[details.product],
        icp_number: address?.icp,
        address_found: address?.icp ? "yes" : "no",
        eligibility: selectedEligibilityType ? "yes" : "maybe",
        registry_load_type: estimatedUsageType,
        customer_type_system: userChecks.isCommercialAccount
          ? "commercial"
          : "residential",
        self_select_load_group: property.usageType,
        payment_success: "success",
        promo_code: promoCode,
        self_select_source: leadSource,
      });
    }
    dispatch(clearState());
    dispatch(setCurrentFormStep(ROUTES.root));
  }, [signUpId]);

  return (
    <section className="layout page">
      <h1>All Done! Ka pai {displayPrefName}.</h1>
      <p className="h3">
        Thanks for adding {displayAddress} to your Flick account.
      </p>
      {fgfSuccess && (
        <strong>
          We've successfully applied the promo code to your application and your
          Friend Get Friend credit will be added to your Flick account.
        </strong>
      )}
      <p>
        Leave the rest to us. We’ll contact your old power provider and switch
        your meter over.
      </p>
      <p>
        If we’re taking over from your current retailer, your power supply won’t
        be affected. If you’re moving into a new property, there’s a small
        chance the previous retailer may disconnect the power during the switch.
        If this is the case at your place, no worries – we’ll reconnect the
        power once you've moved in. 90% of our reconnections can be done
        remotely, so it’s usually a quick and easy process!
      </p>
      <p>
        Our top tip? On your move-in day, check the power bright and early, and
        contact us if it's not working. Please find more information on
        reconnection fees and timeframes in our{" "}
        <a
          href="https://help-flickelectric.force.com/support/s/article/I-need-to-reconnect-the-power-at-my-property"
          className="link"
          target="_blank"
          rel="noopener noreferrer"
        >
          FAQs
        </a>
        .
      </p>
    </section>
  );
}
